import axios from 'axios';
import { TRIGGERS_URL } from './urls.mjs';
import { Property } from '../Components/ProjectConfig/types';
import { GeofenceEntityType, GeoJsonType } from '../util/enums';
import { GridRowData } from '@material-ui/data-grid';
import { GeofenceFilter, NameId } from '../Components/Map/types';
import { ProjectPropertiesDto } from './types/metadata';
import {
  Microfence,
  MicrofenceDto,
  PagiantedMicrofences,
  PagiantedMicrofencesQuery,
  UpdateMicrofenceDto,
} from './types/triggers';
import { MICROFENCE_LAYER_ID } from '../util/constants.js';

/* Properties */
export const getGeomobyProperties = async ({
  clientId,
  projectId,
  Authorization,
}: {
  clientId: string;
  projectId: string;
  Authorization: string;
}) => {
  return (
    await axios.get<Property[]>(`${TRIGGERS_URL}/${clientId}/${projectId}/properties/geomoby`, {
      headers: { Authorization },
    })
  ).data;
};

export const getProjectProperties = async ({
  clientId,
  projectId,
  Authorization,
}: {
  clientId: string;
  projectId: string;
  Authorization: string;
}) => {
  return (
    await axios.get<Property[]>(`${TRIGGERS_URL}/${clientId}/${projectId}/properties/project`, {
      headers: { Authorization },
    })
  ).data;
};

export const createProjectProperties = async ({
  clientId,
  projectId,
  Authorization,
  params,
}: {
  clientId: string;
  projectId: string;
  Authorization: string;
  params: ProjectPropertiesDto;
}) => {
  return (
    await axios.post<Property>(
      `${TRIGGERS_URL}/${clientId}/${projectId}/properties/project`,
      params,
      { headers: { Authorization } },
    )
  ).data;
};

export const updateProjectProperties = async ({
  clientId,
  projectId,
  Authorization,
  id,
  params,
}: {
  clientId: string;
  projectId: string;
  Authorization: string;
  id: string;
  params: ProjectPropertiesDto;
}) => {
  return (
    await axios.patch<Property>(
      `${TRIGGERS_URL}/${clientId}/${projectId}/properties/project/${id}`,
      params,
      { headers: { Authorization } },
    )
  ).data;
};

export const deleteProjectProperties = async ({
  clientId,
  projectId,
  Authorization,
  id,
}: {
  clientId: string;
  projectId: string;
  Authorization: string;
  id: string;
}) => {
  return (
    await axios.delete<Property>(
      `${TRIGGERS_URL}/${clientId}/${projectId}/properties/project/${id}`,
      { headers: { Authorization } },
    )
  ).data;
};

/* Geofences */
export const getLayers = async ({
  clientId,
  projectId,
  Authorization,
}: {
  clientId: string;
  projectId: string;
  Authorization: string;
}) => {
  return (
    await axios.get<NameId[]>(`${TRIGGERS_URL}/${clientId}/${projectId}/geofences`, {
      headers: { Authorization },
    })
  ).data;
};

/* Geofences */
export const getGeofence = async ({
  id,
  layerId,
  type,
  clientId,
  projectId,
  Authorization,
}: {
  id: string;
  layerId: string;
  type: GeofenceEntityType;
  clientId: string;
  projectId: string;
  Authorization: string;
}) => {
  return (
    await axios.get<GridRowData>( // TODO: Add a type here. I will do it when next updating this file.
      `${TRIGGERS_URL}/${clientId}/${projectId}/geofences/${layerId}/${type}/${id}`,
      { headers: { Authorization } },
    )
  ).data;
};

export const paginateGeofences = async ({
  clientId,
  projectId,
  Authorization,
  filter,
  pageNumber,
  perPage,
  signal,
}: {
  clientId: string;
  projectId: string;
  Authorization: string;
  filter: GeofenceFilter | undefined;
  pageNumber: number;
  perPage: number;
  signal?: AbortSignal;
}) => {
  const queryParams = filter
    ? `?${new URLSearchParams(
        Object.entries({
          ...(filter ?? {}),
          perPage: perPage ?? 50,
        })
          .map(([key, val]) => [key, val?.toString() || undefined])
          .filter((entry): entry is [string, string] => entry[1] !== undefined),
      ).toString()}`
    : '';
  return (
    await axios.get<{ geofences: GridRowData[]; count: number }>( // TODO: Add a type here. Created a ticket to generally stop using GridRowData. There will be a bit more to it than just changing it here. https://geomoby.atlassian.net/browse/LTP-1485
      `${TRIGGERS_URL}/${clientId}/${projectId}/geofences/paginate/${pageNumber}${queryParams}`,
      { headers: { Authorization }, signal },
    )
  ).data;
};

export const makeMultipolygonValid = async ({
  coordinates,
  layerId,
  clientId,
  projectId,
  Authorization,
}: {
  coordinates: number[][][][];
  layerId: string;
  clientId: string;
  projectId: string;
  Authorization: string;
}) => {
  return (
    await axios.post<{ type: GeoJsonType.Multipolygon; coordinates: number[][][][] }>(
      `${TRIGGERS_URL}/${clientId}/${projectId}/geofences/${layerId}/multipolygon/make-valid`,
      { type: GeoJsonType.Multipolygon, coordinates },
      { headers: { Authorization } },
    )
  ).data;
};

/* Microfences */
export const paginateMicrofences = async ({
  clientId,
  projectId,
  Authorization,
  query,
}: {
  clientId: string;
  projectId: string;
  Authorization: string;
  query?: PagiantedMicrofencesQuery;
}) => {
  const queryParams = query
    ? `?${new URLSearchParams(
        Object.entries({
          ...(query ?? {}),
          perPage: query?.perPage ?? 50,
        })
          .map(([key, val]) => [key, val?.toString() || undefined])
          .filter((entry): entry is [string, string] => entry[1] !== undefined),
      ).toString()}`
    : '';
  const result = (
    await axios.get<PagiantedMicrofences>(
      `${TRIGGERS_URL}/${clientId}/${projectId}/microfences/paginate${queryParams}`,
      { headers: { Authorization } },
    )
  ).data;

  const { info, results } = result;
  return {
    info,
    results: results.map(microfence => {
      return {
        ...microfence,
        layerId: MICROFENCE_LAYER_ID,
      };
    }) as MicrofenceDto[],
  };
};

export const getMicrofence = async ({
  clientId,
  projectId,
  Authorization,
  uuid,
}: {
  clientId: string;
  projectId: string;
  Authorization: string;
  uuid: string;
}) => {
  return (
    await axios.get<Microfence>(`${TRIGGERS_URL}/${clientId}/${projectId}/microfences/${uuid}`, {
      headers: { Authorization },
    })
  ).data;
};

export const updateMicrofence = async ({
  clientId,
  projectId,
  Authorization,
  uuid,
  updates,
}: {
  clientId: string;
  projectId: string;
  Authorization: string;
  uuid: string;
  updates: UpdateMicrofenceDto;
}) => {
  return (
    await axios.patch<Microfence>(
      `${TRIGGERS_URL}/${clientId}/${projectId}/microfences/${uuid}`,
      updates,
      { headers: { Authorization } },
    )
  ).data;
};

export const deleteMicrofence = async ({
  clientId,
  projectId,
  Authorization,
  uuid,
}: {
  clientId: string;
  projectId: string;
  Authorization: string;
  uuid: string;
}) => {
  return (
    await axios.delete<Microfence>(`${TRIGGERS_URL}/${clientId}/${projectId}/microfences/${uuid}`, {
      headers: { Authorization },
    })
  ).data;
};
